/**
 * Caregiver reminders — the answer to "parents miss vaccination schedules".
 *
 * A record reaching the registry settles the past. It does nothing for the next
 * dose unless someone tells the family when and where to come. This module works
 * out that visit from the same schedule rules the validator enforces, and plans
 * the messages that go with it.
 *
 * Pure functions only. Nothing here sends anything: the plan is what a gateway
 * would be handed, and the prototype shows it rather than transmitting it.
 */

import {
  SCHEDULE, SCHEDULE_BY_KEY, MIN_INTERVAL_DAYS, addDays, daysBetween, isIsoDate,
  type DoseInput, type ScheduleEntry
} from './schedule.js';
import { invalidDoseAntigen } from './validation.js';

/**
 * Outreach sessions at one site recur on a fixed cycle. Four weeks is the
 * working assumption for a union-level outreach spot; it is a parameter so a
 * site with a different cycle is a configuration change, not a code change.
 */
export const DEFAULT_SESSION_CYCLE_DAYS = 28;

/** Days after a missed visit before the health worker is asked to follow up. */
export const ESCALATE_AFTER_DAYS = 3;

/**
 * The earliest date each outstanding dose may validly be given.
 *
 * First doses and single-dose vaccines: the scheduled minimum age. Later doses:
 * the later of the scheduled age and 28 days after the previous dose — given,
 * or itself still to come.
 */
export function earliestValidDates(dob: string, doses: DoseInput[]): Map<ScheduleEntry, string> {
  const out = new Map<ScheduleEntry, string>();
  if (!isIsoDate(dob)) return out;
  const given = new Map(doses.filter(d => isIsoDate(d.givenOn)).map(d => [d.antigen, d.givenOn]));
  const expected = new Map<string, string>();
  for (const s of SCHEDULE) {
    let earliest = addDays(dob, s.dueDays);
    if (s.series && s.n > 1) {
      const prev = SCHEDULE.find(p => p.series === s.series && p.n === s.n - 1)!;
      const prevOn = given.get(prev.key) ?? expected.get(prev.key);
      if (prevOn) {
        const byInterval = addDays(prevOn, MIN_INTERVAL_DAYS);
        if (byInterval > earliest) earliest = byInterval;
      }
    }
    const done = given.get(s.key);
    if (done) { expected.set(s.key, done); continue; }
    expected.set(s.key, earliest);
    out.set(s, earliest);
  }
  return out;
}

/**
 * The doses that count. A dose the worker accepted as invalid — Penta-3 given
 * 18 days after Penta-2, Penta-1 given at four weeks old — stays on the card and
 * the record, but it "does not count and will be repeated". Planning from it as
 * if it were given would mean the family is never told to come back for it.
 *
 * The doses of a series that do count are then numbered again in date order.
 * If Penta-1 was too early, the Penta given at the second visit is the child's
 * first valid Penta dose: what is due next is the second, not a repeat of the
 * first alongside the third on the same day.
 */
export function countedDoses<D extends DoseInput>(
  doses: D[], resolutions: { flagCode: string; kind: string }[]
): D[] {
  const invalid = new Set(resolutions
    .filter(r => r.kind === 'accept-invalid')
    .map(r => invalidDoseAntigen(r.flagCode)));
  const valid = doses.filter(d => !invalid.has(d.antigen));
  const bySeries = new Map<string, D[]>();
  for (const d of valid) {
    const series = SCHEDULE_BY_KEY[d.antigen]?.series;
    if (series) bySeries.set(series, [...(bySeries.get(series) ?? []), d]);
  }
  const renamed = new Map<D, string>();
  for (const [series, list] of bySeries) {
    [...list].sort((a, b) => a.givenOn.localeCompare(b.givenOn)).forEach((d, i) => {
      const entry = SCHEDULE.find(s => s.series === series && s.n === i + 1);
      if (entry) renamed.set(d, entry.key);
    });
  }
  return valid.map(d => renamed.has(d) && renamed.get(d) !== d.antigen ? { ...d, antigen: renamed.get(d)! } : d);
}

export interface NextVisit {
  /** The outreach session the family should attend. */
  date: string;
  /** Doses that will be valid on that date. */
  doses: ScheduleEntry[];
  /** True if at least one of them is already late. */
  catchUp: boolean;
  /** Days from today. */
  inDays: number;
}

/** The first session on or after `from`, on a cycle anchored at `anchor`. */
export function sessionOnOrAfter(from: string, anchor: string, cycleDays = DEFAULT_SESSION_CYCLE_DAYS): string {
  const gap = daysBetween(anchor, from);
  const cycles = Math.max(0, Math.ceil(gap / cycleDays));
  return addDays(anchor, cycles * cycleDays);
}

/**
 * The next session this child should attend, and what they will receive there.
 * Null when the schedule is complete or the date of birth is unreadable.
 *
 * `asOf` is today's session: a dose due today was offered today, so the next
 * visit is looked for strictly after it.
 */
export function nextVisit(
  dob: string, doses: DoseInput[], asOf: string, cycleDays = DEFAULT_SESSION_CYCLE_DAYS
): NextVisit | null {
  const earliest = earliestValidDates(dob, doses);
  if (!earliest.size) return null;
  const firstEligible = [...earliest.values()].sort()[0]!;
  const floor = firstEligible > asOf ? firstEligible : addDays(asOf, 1);
  const date = sessionOnOrAfter(floor, asOf, cycleDays);
  // One dose per series per visit. A child who never had PCV is due PCV-1 and,
  // on paper, PCV-2 as well — but they cannot be given on the same day. A
  // later dose is offered only once the one before it is actually on record.
  const given = new Set(doses.filter(d => isIsoDate(d.givenOn)).map(d => d.antigen));
  const due = [...earliest.entries()]
    .filter(([s, on]) => on <= date && (!s.series || s.n === 1
      || given.has(SCHEDULE.find(p => p.series === s.series && p.n === s.n - 1)!.key)))
    .map(([s]) => s);
  return {
    date,
    doses: due,
    catchUp: [...earliest.entries()].some(([s, on]) => due.includes(s) && on < asOf),
    inDays: daysBetween(asOf, date)
  };
}

// ── Bangla rendering ─────────────────────────────────────────────────────────

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';
const BN_MONTHS = ['জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'];
const BN_WEEKDAYS = ['রবিবার', 'সোমবার', 'মঙ্গলবার', 'বুধবার', 'বৃহস্পতিবার', 'শুক্রবার', 'শনিবার'];
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EN_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const toBnDigits = (s: string | number): string => String(s).replace(/\d/g, d => BN_DIGITS[+d]!);

export function bnDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${BN_WEEKDAYS[d.getUTCDay()]}, ${toBnDigits(d.getUTCDate())} ${BN_MONTHS[d.getUTCMonth()]}`;
}

export function enDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${EN_WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${EN_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * SMS segments for a message. Bangla needs UCS-2: 70 characters in a single
 * message, 67 per part once it is split. Latin-only text fits 160 / 153.
 */
export function smsSegments(text: string): number {
  const units = [...text].length;
  const unicode = /[^\x00-\x7F]/.test(text);
  const single = unicode ? 70 : 160;
  const part = unicode ? 67 : 153;
  return units <= single ? 1 : Math.ceil(units / part);
}

// ── The plan ─────────────────────────────────────────────────────────────────

export type ReminderKind = 'advance' | 'day_of' | 'missed';
export type ReminderChannel = 'sms' | 'voice' | 'worker_task';

export interface Reminder {
  kind: ReminderKind;
  channel: ReminderChannel;
  /** The day it goes out. */
  on: string;
  /** Who receives it. */
  to: 'caregiver' | 'health_worker';
  textBn: string;
  textEn: string;
  /** Only sent if the visit has not been recorded by then. */
  conditional: boolean;
}

export interface ReminderInput {
  childNameBn: string;
  childName: string;
  dob: string;
  doses: DoseInput[];
  siteBn: string;
  site: string;
  asOf: string;
  /** Present only when a caregiver number was read and confirmed. */
  phone?: string | null;
  cycleDays?: number;
}

export interface ReminderPlan {
  visit: NextVisit | null;
  reminders: Reminder[];
  /** Why no caregiver message can be sent, if none can. */
  blocked: string | null;
}

/** Bangladeshi mobile numbers: 01 followed by an operator digit 3–9 and eight more. */
export function validBdMobile(phone: string | null | undefined): boolean {
  return !!phone && /^01[3-9]\d{8}$/.test(phone.replace(/[\s-]/g, ''));
}

export function reminderPlan(input: ReminderInput): ReminderPlan {
  const visit = nextVisit(input.dob, input.doses, input.asOf, input.cycleDays);
  if (!visit) return { visit, reminders: [], blocked: 'The schedule is complete, or the date of birth could not be read.' };

  const vaxBn = visit.doses.map(d => d.labelBn).join(', ');
  const vaxEn = visit.doses.map(d => d.label).join(', ');
  const name = input.childNameBn || input.childName;
  const reminders: Reminder[] = [];

  const advanceOn = addDays(visit.date, -2);
  if (advanceOn > input.asOf) reminders.push({
    kind: 'advance', channel: 'sms', on: advanceOn, to: 'caregiver', conditional: false,
    textBn: `${name}-এর টিকা ${bnDate(visit.date)}, ${input.siteBn}। টিকা: ${vaxBn}। কার্ড সাথে আনুন।`,
    textEn: `${input.childName}'s vaccination: ${enDate(visit.date)}, ${input.site}. Vaccines: ${vaxEn}. Bring the card.`
  });

  reminders.push({
    kind: 'day_of', channel: 'voice', on: visit.date, to: 'caregiver', conditional: false,
    textBn: `আজ ${name}-এর টিকার দিন। ${input.siteBn}-এ সকাল ৯টা থেকে টিকা দেওয়া হবে।`,
    textEn: `Today is ${input.childName}'s vaccination day. Vaccination at ${input.site} from 9 am.`
  });

  const missedOn = addDays(visit.date, ESCALATE_AFTER_DAYS);
  reminders.push({
    kind: 'missed', channel: 'sms', on: missedOn, to: 'caregiver', conditional: true,
    textBn: `${name}-এর টিকা বাকি আছে। পরের সেশনে অবশ্যই আসুন, অথবা স্বাস্থ্য সহকারীর সাথে যোগাযোগ করুন।`,
    textEn: `${input.childName}'s vaccination is still due. Please come to the next session or contact the health assistant.`
  });
  reminders.push({
    kind: 'missed', channel: 'worker_task', on: missedOn, to: 'health_worker', conditional: true,
    textBn: `বাড়ি পরিদর্শন: ${name} — ${vaxBn} বাকি।`,
    textEn: `Home visit: ${input.childName} missed ${vaxEn} on ${enDate(visit.date)}.`
  });

  const blocked = validBdMobile(input.phone)
    ? null
    : 'No confirmed caregiver mobile. Messages to the family cannot go; the health worker follow-up still stands.';
  return {
    visit,
    reminders: blocked ? reminders.filter(r => r.to === 'health_worker') : reminders,
    blocked
  };
}

// ── The digital card ─────────────────────────────────────────────────────────

/**
 * What the QR code on the digital card carries: what the paper card carries,
 * and nothing it does not. No phone number, no household — a card found in the
 * street should identify no more than the paper one would.
 *
 * Versioned so a scanner years from now can still read it:
 *   EPI1|<id>|<name>|<dob>|<sex>|<antigen>:<yyyymmdd>,…
 *   EPI2|… the same …|<family key>|<server>
 * EPI2 adds what the family's app needs to keep the card up to date by itself:
 * a random key that opens this one child's record, read-only, and the address
 * of the server to ask. Without them the card is a snapshot, and still works.
 */
export interface CardData {
  epiId: string; name: string; dob: string; sex: string; doses: DoseInput[];
  familyKey?: string; server?: string;
}

/** A family key: 128 random bits, URL-safe. Unguessable, so it is the credential. */
export function isFamilyKey(key: string | undefined | null): key is string {
  return !!key && /^[A-Za-z0-9_-]{22}$/.test(key);
}

export function cardPayload(input: CardData): string {
  const order = (a: DoseInput) => SCHEDULE.findIndex(s => s.key === a.antigen);
  const doses = input.doses
    .filter(d => isIsoDate(d.givenOn) && SCHEDULE_BY_KEY[d.antigen])
    .sort((a, b) => order(a) - order(b))
    .map(d => `${d.antigen}:${d.givenOn.replace(/-/g, '')}`)
    .join(',');
  const clean = (s: string) => s.replace(/[|,:]/g, ' ').trim();
  const core = [clean(input.epiId || 'NEW'), clean(input.name), input.dob.replace(/-/g, ''),
    input.sex.slice(0, 1).toUpperCase(), doses];
  return isFamilyKey(input.familyKey) && input.server
    ? ['EPI2', ...core, input.familyKey, input.server.replace(/\|/g, '').replace(/\/+$/, '')].join('|')
    : ['EPI1', ...core].join('|');
}

export function parseCardPayload(payload: string): CardData | null {
  const parts = payload.split('|');
  const v2 = parts[0] === 'EPI2' && parts.length === 8;
  if (!(parts[0] === 'EPI1' && parts.length === 6) && !v2) return null;
  const iso = (s: string) => `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  const card: CardData = {
    epiId: parts[1]!, name: parts[2]!, dob: iso(parts[3]!), sex: parts[4]!,
    doses: parts[5] ? parts[5].split(',').map(x => {
      const [antigen, on] = x.split(':');
      return { antigen: antigen!, givenOn: iso(on!) };
    }) : []
  };
  if (v2 && isFamilyKey(parts[6]) && /^https?:\/\/[^\s]+$/.test(parts[7]!)) {
    card.familyKey = parts[6];
    card.server = parts[7];
  }
  return card;
}

// ── For the family ───────────────────────────────────────────────────────────

export interface NextDue {
  /** The first day the next doses can be given: today, if they are already due. */
  from: string;
  doses: ScheduleEntry[];
  /** Due already: go to the next session you can. */
  dueNow: boolean;
  /** Days until `from`; 0 when due now. */
  inDays: number;
}

/**
 * The next doses and the first day they can be given, without a session
 * calendar. A family's phone does not know when outreach sessions are held; it
 * knows the schedule, and can say "from this date" — the same rules the worker's
 * validator enforces, so it never names a dose too early or two of a series.
 */
export function nextDue(dob: string, doses: DoseInput[], today: string): NextDue | null {
  const visit = nextVisit(dob, doses, addDays(today, -1), 1);
  if (!visit) return null;
  return { from: visit.date, doses: visit.doses, dueNow: visit.date <= today, inDays: Math.max(0, daysBetween(today, visit.date)) };
}

/** A child's age in Bangla: "৭ মাস", "১ বছর ৩ মাস". */
export function bnAge(dob: string, today: string): string {
  const days = daysBetween(dob, today);
  if (!isIsoDate(dob) || days < 0) return '';
  if (days < 42) return `${toBnDigits(Math.floor(days / 7))} সপ্তাহ`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${toBnDigits(months)} মাস`;
  const y = Math.floor(months / 12), m = months % 12;
  return m ? `${toBnDigits(y)} বছর ${toBnDigits(m)} মাস` : `${toBnDigits(y)} বছর`;
}
